import React, { useEffect, useState } from "react";
import { functionsService } from "@/services/functionsService";
import { Card } from "@/components/ui/Card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface PriceChartProps {
  ticker: string;
  days?: number;
}

export const PriceChart: React.FC<PriceChartProps> = ({ ticker, days = 90 }) => {
  const [prices, setPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPrices();
  }, [ticker, days]);

  const loadPrices = async () => {
    try {
      setLoading(true);
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - days);

      const result = await functionsService.getHistoricalPrices(
        ticker,
        from.toISOString().split("T")[0],
        to.toISOString().split("T")[0]
      );

      if (result.success) {
        // Format data for chart (take every 5th point for performance)
        const formatted = result.prices
          .reverse()
          .filter((_: any, i: number) => i % 5 === 0)
          .map((price: any) => ({
            date: new Date(price.date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
            close: price.close,
            volume: price.volume,
          }));
        setPrices(formatted);
      }
    } catch (error) {
      console.error("Failed to load prices:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (prices.length === 0) {
    return (
      <Card className="p-6">
        <p className="body-text text-gray-500">No price data available</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="sub-header text-xl mb-4">Price Chart ({days} days)</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={prices}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis domain={["auto", "auto"]} />
          <Tooltip
            formatter={(value: number) => [`$${value.toFixed(2)}`, "Close"]}
          />
          <Line
            type="monotone"
            dataKey="close"
            stroke="#76B900"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

